import { Link, Outlet, useLocation } from "react-router";

import { PageLayout } from "@/shared/components";
import { Tab, Tabs, TabsList } from "@/shared/foundation/primitives";

import styles from "./AgentsTabs.module.css";
import { AgentTab, useActiveAgentTab } from "./useActiveAgentTabFromUrl";

const AGENT_TABS = [
  { tab: AgentTab.Library, label: "Agents" },
  { tab: AgentTab.Threads, label: "Threads" },
  { tab: AgentTab.Skills, label: "Skills" },
  { tab: AgentTab.Context, label: "Context" },
];

/**
 * Top-level Agents page: parent layout for the `/agents/*` route tree.
 * Renders the page chrome (header, tab strip) and delegates the active
 * tab's body to `<Outlet>`.
 *
 * The active tab is derived from the trailing URL segment by
 * `useActiveAgentTab` so deep links into a specific tab survive refresh,
 * and breadcrumbs from descendant pages can target a named tab
 * (`/agents/library`, `/agents/threads`) rather than an index that may
 * redirect elsewhere once additional tabs land.
 *
 * Layout note: the page height flows through a flex chain (page-content →
 * page → body → outlet) so an inner ``DataTable`` can take
 * the remaining vertical space, matching the Datasets / Events tables.
 */
export function AgentsTabs() {
  const [activeTab] = useActiveAgentTab();
  const location = useLocation();

  // Page chrome follows the active tab so the header and browser tab
  // reflect what the user is actually looking at — "Agents" for the
  // library, "Agent Context" for the org-knowledge / MCP form, and
  // "Agent Threads" for the threads list.
  const headerLabel = (() => {
    switch (activeTab) {
      case AgentTab.Context:
        return "Agent Context";
      case AgentTab.Threads:
        return "Agent Threads";
      case AgentTab.Skills:
        return "Agent Skills";
      case AgentTab.Library:
      default:
        return "Agents";
    }
  })();

  return (
    <PageLayout
      title={`Roboto - ${headerLabel}`}
      headerContentLeft={<>{headerLabel}</>}
    >
      <div className={styles.page}>
        <Tabs value={activeTab}>
          <TabsList aria-label="Agents">
            {AGENT_TABS.map(({ tab, label }) => (
              <Tab
                key={tab}
                value={tab}
                nativeButton={false}
                render={
                  <Link
                    to={{ pathname: tab, search: location.search }}
                    replace
                  />
                }
              >
                {label}
              </Tab>
            ))}
          </TabsList>
        </Tabs>

        <div className={styles.body}>
          <Outlet />
        </div>
      </div>
    </PageLayout>
  );
}
